import { useState } from 'react';
import { supabase } from '@shared/lib/supabase';
import { useAuth } from '@dashboard/hooks/useAuth';

export default function SecurityTab() {
  const { user } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [focusedField, setFocusedField] = useState<string | null>(null);
  const [updateBtnHover, setUpdateBtnHover] = useState(false);

  const handleChangePassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    setError('');
    setSuccess(false);
    setSaving(true);

    const { error: updateError } = await supabase.auth.updateUser({
      password: newPassword,
    });

    setSaving(false);
    if (updateError) {
      setError(updateError.message);
      return;
    }
    setSuccess(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setTimeout(() => setSuccess(false), 3000);
  };

  const sessionCreated = user?.last_sign_in_at
    ? new Date(user.last_sign_in_at).toLocaleString()
    : '--';

  const inputClass = 'w-full rounded px-3 py-2 font-sans text-sm focus:outline-none transition-colors';

  const inputStyle: React.CSSProperties = {
    border: '1px solid var(--dash-border)',
    background: 'var(--dash-input)',
    color: 'var(--dash-text-secondary)',
  };

  const inputFocusStyle: React.CSSProperties = {
    ...inputStyle,
    borderColor: 'var(--dash-active-border)',
  };

  const getInputStyle = (field: string) =>
    focusedField === field ? inputFocusStyle : inputStyle;

  return (
    <div className="space-y-5">
      <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
        <h2 className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'var(--dash-text-muted)' }}>
          Change Password
        </h2>
        <div className="space-y-3 max-w-md">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest mb-1 block" style={{ color: 'var(--dash-text-muted)' }}>
              Current Password
            </label>
            <input
              type="password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              className={inputClass}
              style={getInputStyle('currentPassword')}
              onFocus={() => setFocusedField('currentPassword')}
              onBlur={() => setFocusedField(null)}
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest mb-1 block" style={{ color: 'var(--dash-text-muted)' }}>
              New Password
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              className={inputClass}
              style={getInputStyle('newPassword')}
              onFocus={() => setFocusedField('newPassword')}
              onBlur={() => setFocusedField(null)}
              placeholder="Min. 8 characters"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-widest mb-1 block" style={{ color: 'var(--dash-text-muted)' }}>
              Confirm Password
            </label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={inputClass}
              style={getInputStyle('confirmPassword')}
              onFocus={() => setFocusedField('confirmPassword')}
              onBlur={() => setFocusedField(null)}
            />
          </div>
          {error && (
            <div className="border border-red-500/30 bg-red-500/10 rounded px-3 py-2">
              <p className="font-sans text-xs text-red-300">{error}</p>
            </div>
          )}
          {success && (
            <div className="border border-emerald-500/30 bg-emerald-500/10 rounded px-3 py-2">
              <p className="font-sans text-xs text-emerald-300">Password updated successfully</p>
            </div>
          )}
          <button
            onClick={handleChangePassword}
            disabled={saving || !newPassword || !confirmPassword}
            className="font-mono text-xs font-medium px-4 py-2 rounded transition-colors disabled:opacity-40 uppercase tracking-wider"
            style={{
              background: updateBtnHover ? 'var(--dash-hover-bg)' : 'var(--dash-card)',
              color: 'var(--dash-text-secondary)',
              border: '1px solid var(--dash-border)',
            }}
            onMouseEnter={() => setUpdateBtnHover(true)}
            onMouseLeave={() => setUpdateBtnHover(false)}
          >
            {saving ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>

      <div className="rounded p-5" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
        <h2 className="font-mono text-[10px] uppercase tracking-widest mb-4" style={{ color: 'var(--dash-text-muted)' }}>
          Session Info
        </h2>
        <dl className="space-y-3 text-sm font-sans">
          <div className="flex justify-between">
            <dt style={{ color: 'var(--dash-text-muted)' }}>Email</dt>
            <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>{user?.email || '--'}</dd>
          </div>
          <div className="flex justify-between">
            <dt style={{ color: 'var(--dash-text-muted)' }}>Last Sign In</dt>
            <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-tertiary)' }}>{sessionCreated}</dd>
          </div>
          <div className="flex justify-between">
            <dt style={{ color: 'var(--dash-text-muted)' }}>User ID</dt>
            <dd className="font-mono text-xs" style={{ color: 'var(--dash-text-dim)' }}>{user?.id || '--'}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
